import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, Users, Plus, Trash2, Star, Mail, Eye, Zap, Brain, Target, Dumbbell, Heart, X
} from "lucide-react";
import { toast } from "sonner";
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { useSwipeToClose } from "../hooks/useSwipeToClose";
import { API } from "../utils/constants";

export const CoachesPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user, loading: userLoading } = useUser();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [coachEmail, setCoachEmail] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [adding, setAdding] = useState(false);

  // Coach type configuration with colors
  const coachTypes = [
    { value: 'head_coach', label: 'Head Coach', icon: Users, color: '#8b5cf6', bgLight: 'bg-purple-100', bgDark: 'bg-purple-500/20', textLight: 'text-purple-600', textDark: 'text-purple-400' },
    { value: 'technical', label: 'Technical', icon: Eye, color: '#3b82f6', bgLight: 'bg-blue-100', bgDark: 'bg-blue-500/20', textLight: 'text-blue-600', textDark: 'text-blue-400' },
    { value: 'physical', label: 'Physical', icon: Dumbbell, color: '#22c55e', bgLight: 'bg-green-100', bgDark: 'bg-green-500/20', textLight: 'text-green-600', textDark: 'text-green-400' },
    { value: 'tactical', label: 'Tactical', icon: Brain, color: '#f97316', bgLight: 'bg-orange-100', bgDark: 'bg-orange-500/20', textLight: 'text-orange-600', textDark: 'text-orange-400' },
    { value: 'mental', label: 'Mental', icon: Heart, color: '#ec4899', bgLight: 'bg-pink-100', bgDark: 'bg-pink-500/20', textLight: 'text-pink-600', textDark: 'text-pink-400' },
    { value: 'goalkeeper', label: 'Goalkeeper', icon: Target, color: '#14b8a6', bgLight: 'bg-teal-100', bgDark: 'bg-teal-500/20', textLight: 'text-teal-600', textDark: 'text-teal-400' },
  ];

  const getCoachTypeConfig = (type) => {
    return coachTypes.find(t => t.value === type) || coachTypes[0];
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    const isModalOpen = showAddModal || selectedCoach;
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [showAddModal, selectedCoach]);

  // Swipe handlers
  const addModalSwipe = useSwipeToClose(() => setShowAddModal(false));
  const detailSwipe = useSwipeToClose(() => setSelectedCoach(null));

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      try {
        const res = await axios.get(`${API}/athlete/coaches?user_id=${user.id}`);
        setCoaches(res.data);
      } catch (e) {
        console.error("Error fetching coaches:", e);
      } finally {
        setLoading(false);
      }
    };
    if (!userLoading && user) {
      fetchData();
    }
  }, [user, userLoading]);

  const handleAddCoach = async () => {
    if (!coachEmail.trim()) {
      toast.error("Please enter coach's email");
      return;
    }
    if (!selectedType) {
      toast.error("Please select a coach type");
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/athlete/invite-by-email`, { 
        athlete_id: user?.id, 
        coach_email: coachEmail.trim(),
        coach_type: selectedType
      });
      toast.success("Coach added successfully!");
      // Refresh the list
      const res = await axios.get(`${API}/athlete/coaches?user_id=${user.id}`);
      setCoaches(res.data);
      setShowAddModal(false);
      setCoachEmail('');
      setSelectedType('');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Coach not found with this email");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveCoach = async (connectionId, coachName) => {
    if (!window.confirm(`Remove ${coachName} from your coaches?`)) return;
    try {
      await axios.delete(`${API}/coach/connection/${connectionId}`);
      toast.success("Coach removed");
      setCoaches(coaches.filter(c => c.connection_id !== connectionId));
      setSelectedCoach(null);
    } catch (e) {
      toast.error("Failed to remove coach");
    }
  };

  // Group coaches by type
  const groupedCoaches = coaches.reduce((acc, coach) => {
    const type = coach.coach_type || 'head_coach';
    if (!acc[type]) acc[type] = [];
    acc[type].push(coach);
    return acc;
  }, {});

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-coaches">
      <header className="flex items-center justify-between p-4 pt-6">
        <button data-testid="back-button" onClick={() => navigate("/")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>My Coaches</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-r from-[#493986] to-[#92a7e8]"
          data-testid="add-coach-button"
        >
          <Plus className="w-5 h-5 text-white" />
        </button>
      </header>

      <div className="flex-1 px-4 pb-8 overflow-y-auto">
        {loading ? (
          <div className={`text-center py-12 ${theme.textMuted}`}>Loading coaches...</div>
        ) : coaches.length === 0 ? (
          <div className={`text-center py-12`}>
            <Users className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`} />
            <h3 className={`font-semibold mb-2 ${theme.textPrimary}`}>No coaches yet</h3>
            <p className={`text-sm mb-4 ${theme.textMuted}`}>Add coaches by their email to get feedback</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl"
            >
              Add Coach
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {coachTypes.map(type => {
              const typeCoaches = groupedCoaches[type.value] || [];
              if (typeCoaches.length === 0) return null;
              
              const TypeIcon = type.icon;
              
              return (
                <div key={type.value}>
                  {/* Type Header */}
                  <div className={`flex items-center gap-3 mb-3 p-3 rounded-xl ${isDark ? type.bgDark : type.bgLight}`}>
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: type.color }}
                    >
                      <TypeIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className={`text-lg font-bold ${isDark ? type.textDark : type.textLight}`}>{type.label}</h2>
                      <p className={`text-xs ${theme.textMuted}`}>{typeCoaches.length} coach{typeCoaches.length !== 1 ? 'es' : ''}</p>
                    </div>
                  </div>
                  
                  {/* Coaches in this type */}
                  <div className="space-y-3">
                    {typeCoaches.map((coach) => {
                      const config = getCoachTypeConfig(coach.coach_type);
                      
                      return (
                        <div
                          key={coach.id}
                          onClick={() => setSelectedCoach(coach)}
                          className={`p-4 rounded-2xl ${theme.card} border-l-4 backdrop-blur-xl cursor-pointer hover:scale-[1.01] transition-all`}
                          style={{ borderLeftColor: config.color }}
                          data-testid={`coach-${coach.id}`}
                        >
                          {/* Row 1: Avatar, Name, Type */}
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${config.color}20` }}
                            >
                              <span className="text-lg font-bold" style={{ color: config.color }}>
                                {coach.name?.charAt(0)?.toUpperCase() || 'C'}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className={`font-semibold ${theme.textPrimary}`}>{coach.name}</h3>
                              <div className="flex items-center gap-2 mt-0.5">
                                <config.icon className="w-3.5 h-3.5" style={{ color: config.color }} />
                                <span className={`text-xs ${theme.textMuted}`}>{config.label}</span>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              coach.connection_status === 'accepted' 
                                ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                                : isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'
                            }`}>
                              {coach.connection_status === 'accepted' ? 'Active' : 'Pending'}
                            </span>
                          </div>

                          {/* Row 2: Email */}
                          <div className={`flex items-center gap-2 mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`}>
                            <Mail className={`w-4 h-4 ${theme.textMuted}`} />
                            <span className={`text-sm ${theme.textMuted}`}>{coach.email}</span>
                          </div>

                          {/* Row 3: Stats */}
                          <div className="flex items-center gap-2 mt-3">
                            <div className={`flex-1 h-10 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'} flex items-center justify-center gap-1.5`}>
                              <Users className={`w-3.5 h-3.5 ${theme.textMuted}`} />
                              <span className={`text-sm font-bold ${theme.textPrimary}`}>{coach.athlete_count || 0}</span>
                              <span className={`text-xs ${theme.textMuted}`}>athletes</span>
                            </div>
                            {coach.specialty && (
                              <div className={`flex-1 h-10 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'} flex items-center justify-center`}>
                                <span className={`text-xs ${theme.textMuted}`}>{coach.specialty}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Coach Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddModal(false)} />
          <div 
            className={`relative w-full max-h-[85vh] overflow-y-auto rounded-t-3xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4 pb-8`}
            style={{ 
              transform: `translateY(${addModalSwipe.translateY}px)`,
              transition: addModalSwipe.isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
            onTouchStart={addModalSwipe.onTouchStart}
            onTouchMove={addModalSwipe.onTouchMove}
            onTouchEnd={addModalSwipe.onTouchEnd}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 cursor-grab" />
            <h2 className={`text-xl font-bold mb-2 ${theme.textPrimary}`}>Add Coach</h2>
            <p className={`text-sm mb-6 ${theme.textMuted}`}>Enter your coach's email address</p>
            
            {/* Email Input */}
            <div className="mb-6">
              <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Coach Email</label>
              <div className="relative">
                <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${theme.textMuted}`} />
                <input
                  type="email"
                  placeholder="coach@example.com"
                  value={coachEmail}
                  onChange={(e) => setCoachEmail(e.target.value)}
                  className={`w-full pl-12 pr-4 py-3 rounded-xl border ${theme.input}`}
                  data-testid="coach-email-input"
                />
              </div>
            </div>

            {/* Coach Type Selection */}
            <div className="mb-6">
              <label className={`block text-sm font-medium mb-3 ${theme.textSecondary}`}>Coach Type</label>
              <div className="grid grid-cols-2 gap-2">
                {coachTypes.map(type => {
                  const TypeIcon = type.icon;
                  const isSelected = selectedType === type.value;
                  
                  return (
                    <button
                      key={type.value}
                      onClick={() => setSelectedType(type.value)}
                      className={`p-3 rounded-xl border flex items-center gap-3 transition-all ${
                        isSelected 
                          ? 'border-transparent text-white' 
                          : `${isDark ? type.bgDark : type.bgLight} border ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`
                      }`}
                      style={isSelected ? { backgroundColor: type.color } : {}}
                      data-testid={`coach-type-${type.value}`}
                    >
                      <TypeIcon className={`w-5 h-5 ${isSelected ? 'text-white' : ''}`} style={!isSelected ? { color: type.color } : {}} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-white' : (isDark ? type.textDark : type.textLight)}`}>
                        {type.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Add Button */}
            <button
              onClick={handleAddCoach}
              disabled={adding || !coachEmail.trim() || !selectedType}
              className="w-full py-4 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl disabled:opacity-50"
              data-testid="submit-add-coach"
            >
              {adding ? 'Adding...' : 'Add Coach'}
            </button>
          </div>
        </div>
      )}

      {/* Coach Detail Modal */}
      {selectedCoach && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedCoach(null)} />
          <div 
            className={`relative w-full max-h-[85vh] overflow-y-auto rounded-t-3xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4 pb-8`}
            style={{ 
              transform: `translateY(${detailSwipe.translateY}px)`,
              transition: detailSwipe.isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
            onTouchStart={detailSwipe.onTouchStart}
            onTouchMove={detailSwipe.onTouchMove}
            onTouchEnd={detailSwipe.onTouchEnd}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 cursor-grab" />
            
            {(() => {
              const config = getCoachTypeConfig(selectedCoach.coach_type);
              const TypeIcon = config.icon;
              
              return (
                <>
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-4">
                    <div 
                      className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${config.color}20` }}
                    >
                      <span className="text-3xl font-bold" style={{ color: config.color }}>
                        {selectedCoach.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h2 className={`text-xl font-bold ${theme.textPrimary}`}>{selectedCoach.name}</h2>
                      <div className="flex items-center gap-2 mt-1">
                        <TypeIcon className="w-4 h-4" style={{ color: config.color }} />
                        <span className={`text-sm ${theme.textMuted}`}>{config.label}</span>
                      </div>
                    </div>
                  </div>

                  {/* Type Badge */}
                  <div className="flex items-center gap-2 mb-4">
                    <span 
                      className="text-xs px-3 py-1 rounded-full text-white font-medium"
                      style={{ backgroundColor: config.color }}
                    >
                      {config.label}
                    </span>
                    <span className={`text-xs px-3 py-1 rounded-full ${
                      selectedCoach.connection_status === 'accepted' 
                        ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                        : isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'
                    }`}>
                      {selectedCoach.connection_status === 'accepted' ? '✓ Active' : 'Pending'}
                    </span>
                  </div>

                  {/* Bio */}
                  {selectedCoach.bio && (
                    <p className={`mb-4 ${theme.textSecondary}`}>{selectedCoach.bio}</p>
                  )}

                  {/* Contact Info */}
                  <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl ${isDark ? 'bg-white/10' : 'bg-[#493986]/10'} flex items-center justify-center`}>
                        <Mail className={`w-5 h-5 ${theme.textMuted}`} />
                      </div>
                      <div>
                        <p className={`text-xs ${theme.textMuted}`}>Email</p>
                        <p className={`font-medium ${theme.textPrimary}`}>{selectedCoach.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Stats Card */}
                  <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <p className={`text-2xl font-bold ${theme.textPrimary}`}>{selectedCoach.athlete_count || 0}</p>
                        <p className={`text-xs ${theme.textMuted}`}>Athletes</p>
                      </div>
                      <div className="text-center">
                        <p className={`text-2xl font-bold ${theme.textPrimary}`}>{selectedCoach.experience || '—'}</p>
                        <p className={`text-xs ${theme.textMuted}`}>Experience</p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <button
                    onClick={() => handleRemoveCoach(selectedCoach.connection_id, selectedCoach.name)}
                    className={`w-full py-3 rounded-xl border flex items-center justify-center gap-2 ${isDark ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-200 text-red-500 hover:bg-red-50'}`}
                  >
                    <Trash2 className="w-5 h-5" />
                    Remove Coach
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachesPage;
